import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import coverImg from "./../../images/blog/span-of-control.svg";
import mathImg from "./../../images/blog/span-of-control-math.svg";
import FAQ from "../../components/FAQs";
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

/**
 * Span of Control: Why Middle Management Caps Your Team—and How Agentic AI
 * Removes the Ceiling. Original, SEO-focused content with verifiable references.
 */

export default function Blog_SpanOfControl() {
  const title =
    "Span of Control: Why Middle Management Caps Your Team—and How Agentic AI Removes the Ceiling";
  const description =
    "Span of control is the hidden ceiling on every team: how many people and threads one leader can actually coordinate. Learn why the limit exists, why orgs add middle-management layers to work around it, and how agentic AI raises the ratio safely with BossDrop.";
  const canonicalUrl = "https://bossdrop.ai/blog/span-of-control-agentic-ai";
  const published = "2026-07-28";
  const readTime = "11 min read";

  // SEO keyword cluster (base BossDrop terms + span-of-control intent)
  const keywords = [
    "bossdrop",
    "bossdrop ai",
    "bossdrop.ai",
    "bossdrop company",
    "span of control",
    "management ratio",
    "span of management",
    "flat organization",
    "flat org structure",
    "delayering",
    "org design",
    "organizational structure",
    "middle management",
    "manager to employee ratio",
    "layers of management",
    "workflow management",
    "workflow automation",
    "ai workflow automation",
    "agentic automation",
    "ai agent automation",
    "autonomous work management",
    "operations automation",
    "ai for project management",
    "ai project management",
    "work management",
    "coordination overhead",
    "corporate bloat",
    "scaling teams",
    "do more with less",
    "ai tools for business",
  ];

  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(coverImg));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(coverImg));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(coverImg),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "Organizational Design",
    });

    injectLDJSON("ldjson-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is span of control?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Span of control (also called the management ratio) is the number of direct reports a single manager oversees. You calculate the average by dividing total direct reports by total supervisors. Wider spans mean flatter orgs; narrower spans mean more management layers.",
          },
        },
        {
          "@type": "Question",
          name: "How does AI increase span of control?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most of a manager's day is coordination work: gathering status, chasing blockers, assembling context, routing decisions, and scheduling. Agentic AI automates that coordination layer, so one leader can oversee far more people and threads without burning out—raising the effective span of control.",
          },
        },
        {
          "@type": "Question",
          name: "Does a wider span of control mean firing managers?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. It means managers stop spending their time on status-chasing and routing, and spend it on judgment, coaching, and strategy. Agentic AI removes the coordination tax that forced companies to add middle-management layers in the first place.",
          },
        },
      ],
    });

    // reveal on scroll
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="page container">
      {/* Breadcrumbs */}
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">Span of Control</span>
      </nav>

      {/* Feature hero */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverImg}
              alt="One leader coordinating a wide team through the BossDrop AI coordination layer, illustrating a large span of control"
            />
          </div>

          <div className="hero-text">
            <div className="pill">ORG DESIGN</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button
                  aria-label="Share on X"
                  onClick={() => onShare("x")}
                  title="Share on X"
                >
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                  title="Share on LinkedIn"
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                  title="Share on Facebook"
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        {/* CTA bar */}
        <div className="hero-cta">
          <span>
            The real limit on your team isn't talent—it's{" "}
            <strong>span of control</strong>. BossDrop AI raises it by automating
            the <strong>coordination layer</strong>.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      {/* ---- Article ---- */}
      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          Ask why a company has so many managers and you'll usually hear a story
          about strategy or seniority. The real answer is quieter and more
          mechanical: <strong>span of control</strong>—the number of people and
          threads one person can actually keep coordinated before things start
          slipping.
          <br />
          <br />
          When that number is low, growth forces you to add layers: team leads,
          coordinators, managers of managers. Each layer is a workaround for a
          coordination limit, not a source of value. This post explains where
          the ceiling comes from, why it drives{" "}
          <strong>middle-management</strong> bloat, and how{" "}
          <strong>agentic AI</strong> raises the ceiling—so a single leader can
          run a much wider team without drowning in status updates.
        </p>

        <nav
          className="toc reveal"
          style={{ "--delay": "100ms" }}
          aria-label="Table of contents"
        >
          <a href="#definition">What span of control is (and the math)</a>
          <a href="#ceiling">Why the ceiling exists</a>
          <a href="#layers">How the ceiling creates middle-management layers</a>
          <a href="#pattern">The pattern nobody names</a>
          <a href="#agentic">How agentic AI raises the ceiling</a>
          <a href="#human">What stays human</a>
          <a href="#playbook">A rollout playbook you can use</a>
          <a href="#resources">External resources</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2 id="definition" className="reveal" style={{ "--delay": "120ms" }}>
          What span of control is (and the math)
        </h2>
        <p className="reveal" style={{ "--delay": "140ms" }}>
          <strong>Span of control</strong>—sometimes called the{" "}
          <em>management ratio</em>—is simply how many direct reports one manager
          oversees. Averaged across a company, it's the total number of people
          divided by the total number of supervisors.
        </p>

        <div className="panel reveal" style={{ "--delay": "160ms" }}>
          <p>
            <strong>Span of control =</strong> direct reports ÷ supervisors.
          </p>
          <p className="muted">
            100 people reporting to 10 managers is an average span of 10. Widen
            the span and the org gets flatter; narrow it and you add layers. A
            span that's too wide burns managers out; a span that's too narrow
            wastes payroll on coordination.
          </p>
        </div>

        <h2 id="ceiling" className="reveal" style={{ "--delay": "180ms" }}>
          Why the ceiling exists
        </h2>
        <p className="reveal" style={{ "--delay": "200ms" }}>
          The ceiling isn't about how many <em>names</em> a manager can remember.
          It's about how many <strong>relationships between the work</strong>{" "}
          they can track. A century ago, management theorists noticed the load
          doesn't grow with your headcount—it grows with the{" "}
          <em>connections</em> between people. Add one person and you don't add
          one relationship; you add all the new pairings and cross-dependencies
          that person is part of.
          <br />
          <br />
          That's why coordination gets heavy so fast. Every extra report means
          more one-on-ones, more context to hold, more handoffs to watch, more
          "wait, who owns this?" moments. Past a point, the manager stops
          leading and starts firefighting.
        </p>

        <ul className="reveal checks" style={{ "--delay": "220ms" }}>
          <li>✅ Status lives in five tools and nobody's head has the whole picture.</li>
          <li>✅ The manager becomes the human router: "ask so-and-so," "loop in this team."</li>
          <li>✅ Blockers surface in a meeting days after they actually started.</li>
          <li>✅ Adding one more report noticeably degrades attention to the rest.</li>
        </ul>

        <h2 id="layers" className="reveal" style={{ "--delay": "240ms" }}>
          How the ceiling creates middle-management layers
        </h2>
        <p className="reveal" style={{ "--delay": "260ms" }}>
          When a team outgrows one manager's span, the default fix is to insert a
          layer: split into pods, add team leads, then add a manager over the
          leads. It works—but every layer you add to protect span of control
          also adds distance between decisions and reality.
        </p>

        <div className="flow reveal" style={{ "--delay": "280ms" }}>
          <div className="step">
            <div className="badge">1</div>
            <div>
              <strong>Signal travels further.</strong> A blocker on the front line
              now climbs several rungs before anyone empowered to act hears about
              it—and the decision has to climb back down.
            </div>
          </div>
          <div className="step">
            <div className="badge">2</div>
            <div>
              <strong>Context gets lossy.</strong> Each handoff is a summary of a
              summary. Nuance evaporates; the wrong thing gets prioritized.
            </div>
          </div>
          <div className="step">
            <div className="badge">3</div>
            <div>
              <strong>Cost compounds.</strong> Layers are the most expensive
              headcount you have, and much of their day is spent rolling status
              up and pushing decisions down.
            </div>
          </div>
          <div className="step">
            <div className="badge">4</div>
            <div>
              <strong>Speed dies quietly.</strong> Nobody decides to be slow. Slow
              is just the sum of all those extra hops—a tax you pay on every
              decision.
            </div>
          </div>
        </div>

        <div className="callout info reveal" style={{ "--delay": "300ms" }}>
          <strong>Middle management is often a coordination workaround.</strong>{" "}
          A lot of "management" headcount exists to move information and
          decisions between people—not to lead them. That's the part software can
          now do.
        </div>

        <h2 id="pattern" className="reveal" style={{ "--delay": "320ms" }}>
          The pattern nobody names
        </h2>
        <p className="reveal" style={{ "--delay": "340ms" }}>
          This isn't new. Every wave of information technology has quietly widened
          span of control. As tools got better at collecting, organizing, and
          presenting operational information, senior leaders found they could
          oversee more people with fewer managers in between—the classic case for{" "}
          <em>delayering</em>. Spreadsheets, dashboards, and project tools each
          nudged the ratio up.
          <br />
          <br />
          The catch: those tools still needed a human to read them, interpret
          them, chase the exceptions, and route the decision. They raised the
          ceiling a little. <strong>Agentic AI raises it a lot</strong>, because
          for the first time the coordination work itself—not just the
          reporting—can be done by software.
        </p>

        <h2 id="agentic" className="reveal" style={{ "--delay": "360ms" }}>
          How agentic AI raises the ceiling (safely)
        </h2>
        <p className="reveal" style={{ "--delay": "380ms" }}>
          The trick isn't "let AI manage people." It's letting AI run the{" "}
          <strong>coordination layer</strong> as a governed control loop:{" "}
          <strong>detect → decide → act → verify</strong>. The leader keeps
          intent, judgment, and final approval; the agent absorbs the routing and
          context work that used to require an extra layer of humans.
        </p>

        <figure className="reveal" style={{ "--delay": "400ms" }}>
          <img
            src={mathImg}
            alt="Before: a four-layer management pyramid where signals climb and descend slowly. After: an AI-flattened org where one leader sets intent and the BossDrop AI layer coordinates a wide team directly."
            style={{ width: "100%", height: "auto", borderRadius: 14 }}
            loading="lazy"
          />
          <figcaption className="muted" style={{ marginTop: 8 }}>
            The same team, two structures. AI absorbs the coordination that used
            to require a middle-management layer—so the span widens and the
            layers collapse.
          </figcaption>
        </figure>

        <div className="panel panelDark reveal" style={{ "--delay": "420ms" }}>
          <h3 className="h2">What BossDrop absorbs from the coordination layer</h3>
          <ul className="bullets">
            <li>
              <strong>Status roll-ups:</strong> pull the real state of work from
              chat, tasks, and calendars—no "quick sync" required.
            </li>
            <li>
              <strong>Blocker detection:</strong> spot overdue work, slipping
              dependencies, and risks the moment they appear, not in the next
              meeting.
            </li>
            <li>
              <strong>Context packaging:</strong> assemble a decision-ready brief—
              owner, impact, options, recommended next step.
            </li>
            <li>
              <strong>Decision routing:</strong> send the right call to the right
              accountable person with a clean approve/deny action.
            </li>
            <li>
              <strong>Execution:</strong> create and update tasks, ping owners
              with deadlines, and schedule only the meetings that truly matter.
            </li>
            <li>
              <strong>Audit trail:</strong> what triggered it, what changed, and
              who approved it—so wider spans stay accountable.
            </li>
          </ul>
          <p className="muted">
            BossDrop sits above your existing <em>project management tool</em> and
            chat stack as an <em>autonomous work management</em> layer. You don't
            migrate; you widen the span.
          </p>
        </div>

        <div className="callout info reveal" style={{ "--delay": "440ms" }}>
          <strong>The new math:</strong> if 60% of a manager's week is
          coordination and AI handles most of it, the same person can lead a
          meaningfully wider team—without the burnout that a wide span used to
          cause.
        </div>

        <h2 id="human" className="reveal" style={{ "--delay": "460ms" }}>
          What stays human
        </h2>
        <p className="reveal" style={{ "--delay": "480ms" }}>
          Widening span of control with AI doesn't make leadership less
          important—it makes the <em>human</em> parts of it more concentrated.
          What agents can't do:
        </p>
        <ul className="bullets reveal" style={{ "--delay": "500ms" }}>
          <li>
            <strong>Set intent and priorities.</strong> Deciding what matters is
            still a person's job.
          </li>
          <li>
            <strong>Coach and develop people.</strong> Growth, trust, and hard
            feedback don't automate.
          </li>
          <li>
            <strong>Own high-stakes judgment.</strong> Agents propose;
            accountable humans approve.
          </li>
          <li>
            <strong>Hold the relationships.</strong> Culture and morale live
            between people, not in a workflow.
          </li>
        </ul>

        <h2 id="playbook" className="reveal" style={{ "--delay": "520ms" }}>
          A rollout playbook you can use (30–60 minutes)
        </h2>
        <ol className="reveal numbered" style={{ "--delay": "540ms" }}>
          <li>
            <strong>Measure your real span.</strong> Divide people by managers.
            Then estimate how much of a manager's week is coordination vs.
            judgment—most are surprised.
          </li>
          <li>
            <strong>Pick one coordination loop to offload.</strong> Status
            roll-ups, blocker escalation, or "who owns this" routing are great
            first targets.
          </li>
          <li>
            <strong>Start in propose mode.</strong> Let the agent draft the
            escalation, the assignment, or the schedule; a human approves.
          </li>
          <li>
            <strong>Set guardrails.</strong> Scoped permissions, explicit
            approvals for high-impact actions, and an audit trail on everything.
          </li>
          <li>
            <strong>Widen deliberately.</strong> As trust grows, add reports or
            remove a layer—and watch decision speed, not just the org chart.
          </li>
        </ol>

        <div className="cta-row reveal" style={{ "--delay": "560ms" }}>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            Widen your span with BossDrop
          </a>
          <Link className="btn btnGhost" to="/how-it-works">
            See how it works
          </Link>
        </div>

        <h2 id="resources" className="reveal" style={{ "--delay": "580ms" }}>
          External resources worth reading
        </h2>
        <p className="reveal" style={{ "--delay": "600ms" }}>
          For deeper background on span of control, management ratios, and
          delayering:
        </p>
        <ul className="bullets reveal" style={{ "--delay": "620ms" }}>
          <li>
            Span of control—definition, history, and the math{" "}
            <a
              href="https://en.wikipedia.org/wiki/Span_of_control"
              target="_blank"
              rel="noopener noreferrer"
            >
              (Wikipedia)
            </a>
          </li>
          <li>
            How IT let leaders manage more people with fewer managers{" "}
            <a
              href="https://www.encyclopedia.com/social-sciences-and-law/economics-business-and-labor/businesses-and-occupations/span-control"
              target="_blank"
              rel="noopener noreferrer"
            >
              (Encyclopedia.com)
            </a>
          </li>
          <li>
            Optimizing span of control for efficiency and growth{" "}
            <a
              href="https://www.onemodel.co/blog/span-of-control"
              target="_blank"
              rel="noopener noreferrer"
            >
              (OneModel)
            </a>
          </li>
          <li>
            Should you delayer your org structure?{" "}
            <a
              href="https://managementkits.com/blog/2020/6/25/should-you-delayer-your-organization"
              target="_blank"
              rel="noopener noreferrer"
            >
              (Management Kits)
            </a>
          </li>
          <li>
            Span of control and organizational structure, explained{" "}
            <a
              href="https://theorgchart.com/resources/span-of-control-and-organizational-structure/"
              target="_blank"
              rel="noopener noreferrer"
            >
              (The Org Chart)
            </a>
          </li>
        </ul>

        <div className="panel reveal" style={{ "--delay": "640ms" }}>
          <h3 className="h2">Related BossDrop posts</h3>
          <ul className="bullets">
            <li>
              <Link to="/blog/decision-latency">
                Decision Latency: The Silent Killer of Execution—and How Agentic
                AI Fixes It
              </Link>
            </li>
            <li>
              <Link to="/blog/managerless-sprint-bossdrop-ai-2029">
                The Managerless Sprint (2029): What AI Automates and What Stays
                Human
              </Link>
            </li>
            <li>
              <Link to="/blog/automation-debt-workflow-sprawl">
                Automation Debt: The Hidden Tax of Workflow Sprawl
              </Link>
            </li>
          </ul>
          <p className="muted">
            Span of control is the "why" behind the org chart. Decision latency
            is what happens when it's too narrow.
          </p>
        </div>

        <FAQ
          reveal={false}
          items={[
            {
              q: "What is a good span of control?",
              a: "There's no universal number—it depends on how coordination-heavy the work is. Complex, interdependent work needs a narrower span; routine or well-instrumented work supports a wider one. The point of AI is to shift that constraint: when software handles coordination, the same manager can support a wider span without losing quality.",
            },
            {
              q: "Is widening span of control just a way to cut managers?",
              a: "It's a way to stop spending management time on status-chasing and routing. Some orgs will remove layers; others will keep their people and redeploy them to coaching, strategy, and customers. The goal is fewer coordination hops, not fewer humans for its own sake.",
            },
            {
              q: "How is this different from a project management tool?",
              a: "Project tools store the work; they still need a human to read them, chase exceptions, and route decisions. BossDrop is an autonomous work-management layer that does the coordination itself—detecting, packaging, routing, and executing with approvals—so span of control can actually widen.",
            },
            {
              q: "Is it safe to let AI run coordination for a bigger team?",
              a: "Yes, with governance: scoped permissions, explicit approvals for high-impact actions, and a full audit trail. Wider spans are only safe when every automated action is logged and reversible—which is exactly how BossDrop is designed.",
            },
            {
              q: "Do we have to replace our current stack?",
              a: "No. BossDrop connects your chat, tasks, and calendar and sits above them. You keep your tools; you remove the manual coordination between them.",
            },
          ]}
        />

        <div className="panel reveal" style={{ "--delay": "700ms" }}>
          <div className="kpis">
            <span className="pill">span of control</span>
            <span className="pill">flat organization</span>
            <span className="pill">autonomous work management</span>
            <span className="pill">agentic automation</span>
          </div>
          <p className="muted">
            Don't add a layer to manage the work. Widen the span and let{" "}
            <strong>coordination become software</strong>.
          </p>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content,
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content,
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content,
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- inline SVGs --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
